import { useCallback, useEffect, useRef, useState } from 'react';

import { useLocalPlayerStore, type LocalTrack } from '@maestra/core/stores/localPlayerStore';

// O PLAYER GLOBAL, sem o desenho: o <audio>, a sincronia com o store e os controles.
//
// Veio inteiro do antigo `LocalPlayerBar`. `playing` no store apenas REFLETE o <audio> (pelos
// eventos), para a linha do catálogo exibir e controlar em sincronia. O controle do áudio é
// IMPERATIVO (`alternar`): um efeito que "sincronizasse" playing→áudio criava um laço play/pause.

// O tempo em texto mora no núcleo desde 31/10/2026: o player do app escreve igual.
export { formatarTempo, useTempoDoPlayer } from '@maestra/core/telas/musicas/tempoDoPlayer';

export const usePlayerLocal = (faixas: LocalTrack[], atual: string, trocar: (id: string) => void) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const tocando = useLocalPlayerStore((s) => s.playing);
  const escondido = useLocalPlayerStore((s) => s.escondido);
  const setPlaying = useLocalPlayerStore((s) => s.setPlaying);
  const setStoreTime = useLocalPlayerStore((s) => s.setTime);
  const setStoreDuration = useLocalPlayerStore((s) => s.setDuration);
  const setStoreCurrentId = useLocalPlayerStore((s) => s.setCurrentId);
  const setStoreToggle = useLocalPlayerStore((s) => s.setToggle);
  const setStoreSeek = useLocalPlayerStore((s) => s.setSeek);
  const [tempo, setTempo] = useState(0);
  const [duracao, setDuracao] = useState(0);
  const [volume, setVolume] = useState(1);
  const [mudo, setMudo] = useState(false);

  const indice = faixas.findIndex((t) => t.id === atual);
  const faixa = faixas[indice];
  const tocavel = !!faixa?.url;

  const alternar = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || !faixa?.url) return;
    if (audio.paused) audio.play().catch(() => {});
    else audio.pause();
  }, [faixa?.url]);

  const irPara = useCallback((valor: number) => {
    const audio = audioRef.current;
    if (!audio || !faixa?.url) return;
    audio.currentTime = Math.max(0, Math.min(valor, audio.duration || valor));
    setTempo(audio.currentTime);
    setStoreTime(audio.currentTime);
  }, [setStoreTime, faixa?.url]);

  // Publica no store a faixa atual e as funções de tocar e de pular, para a lista controlar o
  // mesmo <audio>. Limpa ao desmontar.
  useEffect(() => {
    setStoreCurrentId(atual);
  }, [atual, setStoreCurrentId]);
  useEffect(() => {
    setStoreToggle(alternar);
    return () => setStoreToggle(null);
  }, [alternar, setStoreToggle]);
  useEffect(() => {
    setStoreSeek(irPara);
    return () => setStoreSeek(null);
  }, [irPara, setStoreSeek]);

  // (Re)carrega e toca quando a faixa muda (o evento onPlay atualiza o store).
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !faixa) return;
    if (!faixa.url) {
      audio.removeAttribute('src');
      audio.load();
      setPlaying(false);
      setTempo(0);
      setDuracao(0);
      setStoreTime(0);
      setStoreDuration(0);
      return;
    }
    audio.src = faixa.url;
    audio.volume = mudo ? 0 : volume;
    audio.play().catch(() => {});
    setTempo(0);
    setDuracao(0);
    setStoreTime(0);
    setStoreDuration(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [faixa?.id]);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) audio.volume = mudo ? 0 : volume;
  }, [volume, mudo]);

  const pular = (dir: -1 | 1) => {
    if (!faixas.length) return;
    const proxima = (indice + dir + faixas.length) % faixas.length;
    trocar(faixas[proxima].id);
  };

  return {
    faixa,
    /** Tocando, mas sem a barra à vista (a playlist aberta controla pela linha). */
    escondido,
    tocando,
    tocavel,
    tempo,
    duracao,
    volume: mudo ? 0 : volume,
    mudo: mudo || volume === 0,
    varias: faixas.length > 1,
    alternar,
    irPara,
    anterior: () => pular(-1),
    proxima: () => pular(1),
    alternarMudo: () => setMudo(!mudo),
    mudarVolume: (v: number) => { setVolume(v); setMudo(false); },
    // O <audio> é do player; a casca só o põe na página com estas propriedades.
    audio: {
      ref: audioRef,
      onPlay: () => setPlaying(true),
      onPause: () => setPlaying(false),
      onTimeUpdate: (e: React.SyntheticEvent<HTMLAudioElement>) => {
        setTempo(e.currentTarget.currentTime);
        setStoreTime(e.currentTarget.currentTime);
      },
      onLoadedMetadata: (e: React.SyntheticEvent<HTMLAudioElement>) => {
        setDuracao(e.currentTarget.duration);
        setStoreDuration(e.currentTarget.duration);
      },
      onEnded: () => (faixas.length > 1 ? pular(1) : setPlaying(false)),
    },
  };
};
